import { CREDIT_REFUSAL_KEYS, type FailedWrite } from '~/features/credit-ledger';
/**
 * **What the session panel's writes say** — the consequence a confirm names before the trainer
 * commits, and the sentence a refusal is answered with after.
 *
 * A confirm body says what the act DOES, never repeats its own question: a no-show that will cost
 * the client a credit says so, a held consultation says what it means for the credit, a
 * forgiveness says what it does to the balance (booking-system R10, R14). Credit sentences are said
 * only while the trainer runs credits (R18) and only when R10's table says the act moves one.
 *
 * A refusal names the act that failed ("Nie odwołano sesji.") and then why, from the code the
 * server sent or, lacking one, from the status — each act has its own reading of a bare 400,
 * because the same status means "already settled" on a cancel and "not over yet" on a no-show.
 *
 * Pure: no React, no DOM, no query hook, no i18next. Words are i18n KEYS.
 */

import type { BookingSession } from './bookingSession';
import { consumesCredit } from './creditConsumption';
import type { ConfirmedAct } from './sessionActions';
import { cancelGivesCreditBack } from './sessionCredit';

const KEY = 'web:booking.actions';

/** The sentences a confirm's body is made of, in order. */
export function actConsequence(
  act: ConfirmedAct,
  session: BookingSession,
  creditsOn: boolean,
): string[] {
  const input = {
    origin: session.origin,
    carriesTrainer: session.carriesTrainer,
  };
  const consumesNow = consumesCredit({ ...input, status: session.wireStatus });
  const consumesAfter = (status: string) => consumesCredit({ ...input, status });
  switch (act) {
    case 'cancel':
      return [
        `${KEY}.cancelBody`,
        ...(creditsOn && cancelGivesCreditBack(session) ? [`${KEY}.creditReturns`] : []),
      ];
    case 'markNoShow':
      return [
        `${KEY}.noShowBody`,
        ...(creditsOn && consumesAfter('NoShow')
          ? [consumesNow ? `${KEY}.noShowKeepsCredit` : `${KEY}.noShowTakesCredit`]
          : []),
      ];
    case 'markHeld':
      return [
        `${KEY}.heldBody`,
        ...(creditsOn && consumesAfter('Completed')
          ? [consumesNow ? `${KEY}.heldUsesCredit` : `${KEY}.heldTakesCredit`]
          : []),
      ];
    case 'forgiveCharge':
      return [`${KEY}.forgiveBody`];
  }
}

/** A refusal: which act failed, and why. */
export interface ActRefusal {
  readonly actKey: string;
  readonly reasonKey: string;
  /** The panel's reads may be behind the server; read them again. */
  readonly refetch: boolean;
  /** The request id a forgiveness carried may not be sent again — mint a new one. */
  readonly remint: boolean;
}

const ACT_FAILED: Readonly<Record<ConfirmedAct, string>> = {
  cancel: `${KEY}.failed.cancel`,
  markNoShow: `${KEY}.failed.markNoShow`,
  markHeld: `${KEY}.failed.markHeld`,
  forgiveCharge: `${KEY}.failed.forgiveCharge`,
};

/** Each act's reading of a refusal with no code: the rule of its own the server most likely held. */
const BARE_400: Readonly<Record<ConfirmedAct, string>> = {
  cancel: `${KEY}.reason.cancelSettled`,
  markNoShow: `${KEY}.reason.noShowNotYet`,
  markHeld: `${KEY}.reason.heldNotYet`,
  forgiveCharge: `${KEY}.reason.nothingToForgive`,
};

export function actRefusal(act: ConfirmedAct, failure: FailedWrite | null): ActRefusal {
  const refusal = (reasonKey: string, refetch: boolean, remint = false): ActRefusal => ({
    actKey: ACT_FAILED[act],
    reasonKey,
    refetch,
    remint,
  });
  if (failure === null) return refusal('web:booking.refusal.unreached', false);
  switch (failure.code) {
    case 'concurrency_conflict':
      return refusal('web:booking.refusal.concurrency_conflict', true);
    case 'credits_disabled':
      return refusal(CREDIT_REFUSAL_KEYS.credits_disabled, true);
    case 'idempotency_key_reused':
      return refusal(CREDIT_REFUSAL_KEYS.idempotency_key_reused, true, true);
    case 'booking_value_not_allowed':
      return refusal(`${KEY}.reason.nothingToForgive`, true);
    default:
      break;
  }
  if (failure.status === 400) return refusal(BARE_400[act], true);
  if (failure.status === 403) return refusal('web:booking.refusal.forbidden', false);
  if (failure.status === 404) return refusal('web:booking.refusal.gone', true);
  if (failure.status === 409) return refusal('web:booking.refusal.concurrency_conflict', true);
  return refusal('web:booking.refusal.other', false);
}
